import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Language } from "../../App";
import {
  loadGoogleMaps,
  buildMarkerIcon,
  getCountryCodeFromAddress,
  extractCityCountry,
  reverseGeocodeCoordinates,
} from "../../utils/locationUtils";

export default function ModalAddLocation({
  isOpen,
  onClose,
  onSave,
  userCoords,
  locationData,
  onCountryDetected,
}) {
  const languageContext = useContext(Language);
  const { lang } = languageContext || { lang: {} };
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const serviceRef = useRef(null);
  const inputRef = useRef(null);

  const [mapsReady, setMapsReady] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const defaultCenter = { lat: 40.4168, lng: -3.7038 };

  useEffect(() => {
    if (!isOpen) return;
    // Doctor/Facility profile pages scroll inside their own fixed-navbar
    // shell (.dp-scroll-area / .fp-scroll-area), not document.body — a
    // class toggle (rather than writing .style directly) avoids fighting
    // those pages' own inline overflowY:'auto' on re-render.
    const scrollAreas = document.querySelectorAll('.dp-scroll-area, .fp-scroll-area, .ip-scroll-area');
    document.body.style.overflow = "hidden";
    scrollAreas.forEach(el => el.classList.add('loc-scroll-locked'));
    return () => {
      document.body.style.overflow = "";
      scrollAreas.forEach(el => el.classList.remove('loc-scroll-locked'));
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    loadGoogleMaps()
      .then(() => {
        setMapsReady(true);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isOpen]);

  const reverseGeocode = useCallback(
    (pos) => {
      reverseGeocodeCoordinates(pos, (locResult) => {
        setSelectedLocation(locResult);
        setSearchValue(locResult.displayText || locResult.address || "");
        if (locResult.countryCode && onCountryDetected) {
          onCountryDetected(locResult.countryCode);
        }
      });
    },
    [onCountryDetected]
  );

  useEffect(() => {
    if (!mapsReady || !mapRef.current || mapInstanceRef.current) return;
    const google = window.google;

    const hasLocationData = Boolean(locationData?.lat && locationData?.lng);
    const hasUserCoords = Boolean(userCoords?.lat && userCoords?.lng);

    const initialPos = hasLocationData
      ? { lat: Number(locationData.lat), lng: Number(locationData.lng) }
      : hasUserCoords
      ? { lat: Number(userCoords.lat), lng: Number(userCoords.lng) }
      : defaultCenter;

    const initialZoom = hasLocationData || hasUserCoords ? 15 : 4;
    const shouldShowMarker = hasLocationData || hasUserCoords;

    const map = new google.maps.Map(mapRef.current, {
      center: initialPos,
      zoom: initialZoom,
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: "greedy",
    });

    const marker = new google.maps.Marker({
      position: initialPos,
      map,
      draggable: true,
      visible: shouldShowMarker,
      icon: buildMarkerIcon(google),
    });

    map.addListener("click", (e) => {
      const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      marker.setPosition(pos);
      marker.setVisible(true);
      reverseGeocode(pos);
    });

    marker.addListener("dragend", (e) => {
      const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      marker.setPosition(pos);
      marker.setVisible(true);
      reverseGeocode(pos);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;
    serviceRef.current = new google.maps.places.AutocompleteService();

    if (hasLocationData) {
      setSelectedLocation(locationData);
      setSearchValue(locationData.displayText || locationData.address || "");
    } else if (hasUserCoords) {
      reverseGeocode(userCoords);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const livePos = { lat: coords.latitude, lng: coords.longitude };
          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.setCenter(livePos);
            mapInstanceRef.current.setZoom(15);
            markerRef.current.setPosition(livePos);
            markerRef.current.setVisible(true);
            reverseGeocode(livePos);
          }
        },
        (err) => {
          console.warn("Geolocation fallback:", err);
          // On permission denial / timeout, do not set any fake address
        },
        { timeout: 6000 }
      );
    }
  }, [mapsReady, locationData, userCoords, reverseGeocode]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    setActiveIdx(-1);
    if (!val.trim() || !serviceRef.current) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    serviceRef.current.getPlacePredictions({ input: val }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
        setSuggestions(predictions);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    });
  };

  const selectSuggestion = useCallback(
    (placeId, description) => {
      setSuggestions([]);
      setShowDropdown(false);
      setActiveIdx(-1);
      if (!window.google || !mapInstanceRef.current) return;
      const svc = new window.google.maps.places.PlacesService(mapInstanceRef.current);
      svc.getDetails(
        { placeId, fields: ["geometry", "formatted_address", "name", "address_components"] },
        (place, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry) return;
          const pos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
          mapInstanceRef.current.setCenter(pos);
          mapInstanceRef.current.setZoom(15);
          if (markerRef.current) {
            markerRef.current.setPosition(pos);
            markerRef.current.setVisible(true);
          }

          const { city, country } = extractCityCountry(place.address_components);
          const displayText = city && country ? `${city}, ${country}` : place.formatted_address || place.name;
          const countryCode = getCountryCodeFromAddress(place.address_components);

          setSelectedLocation({
            ...pos,
            address: place.formatted_address || place.name,
            city,
            country,
            displayText,
            countryCode,
          });
          setSearchValue(displayText);
          if (countryCode && onCountryDetected) {
            onCountryDetected(countryCode);
          }
        }
      );
    },
    [onCountryDetected]
  );

  const handleKeyDown = (e) => {
    if (!showDropdown || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = activeIdx >= 0 ? activeIdx : 0;
      if (suggestions[idx]) {
        selectSuggestion(suggestions[idx].place_id, suggestions[idx].description);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleZoomIn = () => mapInstanceRef.current?.setZoom(mapInstanceRef.current.getZoom() + 1);
  const handleZoomOut = () => mapInstanceRef.current?.setZoom(mapInstanceRef.current.getZoom() - 1);

  const handleMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const loc = { lat: coords.latitude, lng: coords.longitude };
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(loc);
          mapInstanceRef.current.setZoom(16);
        }
        if (markerRef.current) {
          markerRef.current.setPosition(loc);
          markerRef.current.setVisible(true);
        }
        reverseGeocode(loc);
      },
      (err) => console.warn("Geolocation:", err),
      { timeout: 6000 }
    );
  };

  const handleSave = () => {
    if (selectedLocation && onSave) {
      onSave({
        ...selectedLocation,
        displayText: selectedLocation.displayText || selectedLocation.address,
      });
    }
  };

  const handleClose = () => {
    mapInstanceRef.current = null;
    markerRef.current = null;
    serviceRef.current = null;
    setMapsReady(false);
    setSearchValue("");
    setSelectedLocation(null);
    setSuggestions([]);
    setShowDropdown(false);
    setActiveIdx(-1);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        /* Freezes the host page's own scroll container while this modal
           is open (see the effect above) — !important is required since
           it has to beat that page's inline overflowY:'auto'. */
        .loc-scroll-locked { overflow: hidden !important; }
        .loc-overlay, .loc-overlay * { font-family: var(--app-font-family) !important; }
        .loc-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; animation: locFadeIn 0.18s ease; }
        @keyframes locFadeIn { from{opacity:0} to{opacity:1} }
        .loc-modal { background: #fff; border-radius: 24px; width: 470px; max-width: 95vw; overflow: hidden; box-shadow: 0 20px 60px rgba(15,23,42,0.18); animation: locUp 0.22s cubic-bezier(.22,.84,.44,1); }
        @keyframes locUp { from { opacity:0; transform:translateY(22px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        .loc-header { padding: 20px 22px 12px; }
        .loc-title { font-size: 13px; font-weight: 700; letter-spacing: 0.09em; color: #0FA9C4; text-transform: uppercase; margin: 0; }
        .loc-map-outer { padding: 0 16px; }
        .loc-map-wrap { position: relative; width: 100%; height: 365px; background: #F9FAFC; border-radius: 16px; overflow: hidden; border: 1px solid #E6E9EF; }
        .loc-map-canvas { width: 100%; height: 100%; }
        .loc-search-wrap { position: absolute; top: 11px; left: 11px; right: 11px; z-index: 20; }
        .loc-search-bar { display: flex; align-items: center; background: #fff; border-radius: 14px; box-shadow: 0 4px 16px rgba(15,23,42,0.12); border: 1px solid #E6E9EF; padding: 0 14px; height: 46px; }
        .loc-search-bar input { flex: 1; border: none; outline: none; font-family: inherit; font-size: 14px; color: #0F172A; background: transparent; }
        .loc-search-bar input::placeholder { color: #A3ACBA; }
        .loc-search-icon { color: #94A3B8; display:flex; align-items:center; margin-left:8px; }
        .loc-suggestions { background: #fff; border-radius: 14px; box-shadow: 0 12px 32px rgba(15,23,42,0.14); border: 1px solid #E6E9EF; margin-top: 6px; overflow: hidden; max-height: 210px; overflow-y: auto; }
        .loc-suggestion-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px; cursor: pointer; transition: background 0.12s; border-bottom: 1px solid #F3F4F6; }
        .loc-suggestion-item:last-child { border-bottom: none; }
        .loc-suggestion-item:hover, .loc-suggestion-item.active { background: #F0FBFD; }
        .loc-sug-icon { flex-shrink:0; margin-top:2px; color:#1DC1DD; }
        .loc-sug-main { font-size:13px; font-weight:600; color:#0F172A; line-height:1.3; }
        .loc-sug-sub  { font-size:11.5px; color:#94A3B8; margin-top:1px; line-height:1.3; }
        .loc-controls { position: absolute; right: 11px; bottom: 11px; z-index: 10; display:flex; flex-direction:column; gap:8px; }
        .loc-ctrl-btn { width:38px; height:38px; background:#fff; border:1px solid #E6E9EF; border-radius:12px; box-shadow:0 4px 12px rgba(15,23,42,0.10); display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:20px; font-weight:600; color:#64748B; transition:all 0.18s ease; line-height:1; }
        .loc-ctrl-btn:hover { background:#F9FAFC; border-color:#1DC1DD; color:#1DC1DD; }
        .loc-ctrl-btn.loc-my { color:#1DC1DD; margin-top:4px; }
        .loc-loading { position:absolute; inset:0; background:#F9FAFC; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; z-index:5; }
        .loc-spinner { width:34px; height:34px; border:3px solid #E6E9EF; border-top-color:#1DC1DD; border-radius:50%; animation:locSpin 0.7s linear infinite; }
        @keyframes locSpin { to{transform:rotate(360deg)} }
        .loc-footer { display: flex; justify-content: flex-end; align-items: center; gap: 12px; padding: 16px 22px 20px; }
        .loc-btn { padding: 11px 26px; border-radius: 999px; font-family: inherit; font-size: 14.5px; font-weight: 700; cursor: pointer; border: none; transition: all 0.2s ease; white-space: nowrap; }
        .loc-btn-cancel { background: #fff; border: 1.5px solid #E6E9EF; color: #64748B; }
        .loc-btn-cancel:hover { background: #F9FAFC; border-color: #1DC1DD; color: #1DC1DD; }
        .loc-btn-save { background: linear-gradient(135deg, #00C8E8 0%, #00BADA 50%, #0088B3 100%); color: #fff; box-shadow: 0 8px 22px rgba(29,193,221,0.32); }
        .loc-btn-save:hover:not(:disabled) { box-shadow: 0 10px 28px rgba(29,193,221,0.42); transform: translateY(-1px); }
        .loc-btn-save:disabled { background: #CBEFF4; box-shadow: none; cursor:not-allowed; transform:none; }
      `}</style>

      <div className="loc-overlay" onClick={(e) => e.stopPropagation()}>
        <div className="loc-modal">
          <div className="loc-header">
            <p className="loc-title">{lang?.location || "LOCATION"}</p>
          </div>

          <div className="loc-map-outer">
            <div className="loc-map-wrap">
              {(loading || !mapsReady) && (
                <div className="loc-loading">
                  <div className="loc-spinner" />
                  <span style={{ fontSize: 13, color: "#64748B" }}>{lang?.loading_maps || "Loading Maps..."}</span>
                </div>
              )}

              <div className="loc-search-wrap">
                <div className="loc-search-bar">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={lang?.search || "Search location..."}
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 180)}
                    onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                    autoComplete="off"
                  />
                  <span className="loc-search-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                </div>

                {showDropdown && suggestions.length > 0 && (
                  <div className="loc-suggestions">
                    {suggestions.map((s, i) => {
                      const main = s.structured_formatting?.main_text || s.description;
                      const sub = s.structured_formatting?.secondary_text || "";
                      return (
                        <div
                          key={s.place_id}
                          className={`loc-suggestion-item${i === activeIdx ? " active" : ""}`}
                          onMouseDown={() => selectSuggestion(s.place_id, s.description)}
                        >
                          <span className="loc-sug-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                            </svg>
                          </span>
                          <div>
                            <div className="loc-sug-main">{main}</div>
                            {sub && <div className="loc-sug-sub">{sub}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div ref={mapRef} className="loc-map-canvas" />

              {mapsReady && (
                <div className="loc-controls">
                  <button className="loc-ctrl-btn" onClick={handleZoomIn} type="button">+</button>
                  <button className="loc-ctrl-btn" onClick={handleZoomOut} type="button">−</button>
                  <button className="loc-ctrl-btn loc-my" onClick={handleMyLocation} type="button" title="My location">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" fill="currentColor" />
                      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                      <circle cx="12" cy="12" r="7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="loc-footer">
            <button className="loc-btn loc-btn-cancel" onClick={handleClose} type="button">
              {lang?.cancel || "Cancel"}
            </button>
            <button
              className="loc-btn loc-btn-save"
              onClick={() => {
                handleSave();
                handleClose();
              }}
              disabled={!selectedLocation}
              type="button"
            >
              {lang?.save || "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
